import type {ReactElement} from 'react';
import React, {useRef, useEffect, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {isThreadsAccount, isGoogleBusinessProfileAccount, isPinterestAccount} from '@Components/social-media/account.vo';
import type {GraphicMeta, PosterHashedId} from '@Components/social-media/publish-params.types';
import {noop} from '@Utils/general.util';
import {getCacheBustedURL} from '@Utils/url.util';
import {doesAccountHaveCarouselPosts, seekAndPauseAutoplayVideo} from '@Libraries/social-media';
import {SimpleMediaCarousel} from '@Components/simple-media-carousel/simple-media-carousel';
import type {MediaItemData} from '@Components/simple-media-carousel/simple-media-carousel.types';
import {isIOS} from 'react-device-detect';
import {pollForPosterPreviewChanges} from '@Libraries/poster-library';
import styles from './social-post-content-media-wrapper.module.scss';
import {PreviewVideoPlayer} from '../../preview-video-player/preview-video-player';
import {ClickableDiv} from '@Components/clickable-div';

export interface SocialPostContentMediaWrapperProps {
  onAssetLoad?: (posterHashedId: string) => void;
  onAssetErrored?: (posterHashedId: string) => void;
  doRetryOnError?: boolean;
  account?: SocialAccount;
  posterHashedIds: string[];
  designsGraphicsMeta: Record<PosterHashedId, GraphicMeta>;
  postersLoadingMap: Record<PosterHashedId, boolean>;
  onOrphanedPreviewContainerClick?: VoidFunction;
}

export function SocialPostContentMediaWrapper({
  onAssetLoad = noop,
  onAssetErrored = noop,
  doRetryOnError = false,
  account,
  posterHashedIds,
  designsGraphicsMeta,
  postersLoadingMap,
  onOrphanedPreviewContainerClick = noop,
}: SocialPostContentMediaWrapperProps): ReactElement<any> {
  const [designsGraphicsMetaState, setDesignsGraphicsMetaState] = useState<Record<PosterHashedId, GraphicMeta>>({});
  const [retryThroughRefreshNumber, setRetryThroughRefreshNumber] = useState(0);
  const [isCacheBustingNeeded, setIsCacheBustingNeeded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const maxNumberDisplayablePosters = 3;
  const maxNumberRows = 2;
  const minNumberRows = 1;
  const minNumberDisplayablePosters = 1;
  const maxRetries = 40;
  const retryTimeoutInMS = 5000;
  const isOrphanedPreview = !account;

  const haveAllPostersLoaded = (): boolean => {
    return Object.values(postersLoadingMap).every((value) => {
      return value;
    });
  };

  useEffect(() => {
    setDesignsGraphicsMetaState({});
  }, [account]);

  const isAnyPosterLoaded = (): boolean => {
    return Object.values(postersLoadingMap).some((value) => {
      return value;
    });
  };

  const shouldResetGraphicsMeta = (): boolean => {
    for (const id in designsGraphicsMeta) {
      const graphicMeta = designsGraphicsMeta[id];
      if (graphicMeta.graphicURL === '') {
        return true;
      }
    }

    return false;
  };

  useEffect((): void => {
    if (haveAllPostersLoaded() || isAnyPosterLoaded()) {
      return;
    }

    if (shouldResetGraphicsMeta()) {
      setDesignsGraphicsMetaState({});
    } else {
      setDesignsGraphicsMetaState(designsGraphicsMeta);
    }
  }, [postersLoadingMap]);

  const getNumberOfImages = (): number => {
    return posterHashedIds.length;
  };

  const isMultiPosting = (): boolean => {
    return posterHashedIds.length > 1;
  };

  const getClassesForHolder = (): string => {
    if (isPinterestAccount(account)) {
      return `${styles.pinterest}`;
    }

    if (isGoogleBusinessProfileAccount(account)) {
      return `${styles.googleBusinessProfile}`;
    }

    if (getNumberOfImages() > minNumberDisplayablePosters) {
      return `${styles.multipleImages}`;
    }

    return '';
  };

  const getClassesForAsset = (imageNumber: number): string => {
    const numberImages = getNumberOfImages();
    if (numberImages === minNumberDisplayablePosters) {
      return '';
    }

    if (imageNumber === 1) {
      return `${styles.multipleImages}`;
    }

    if (numberImages === 2) {
      return `${styles.twinImages}`;
    }

    return `${styles.lastRow}`;
  };

  const isLastRow = (rowIndex: number): boolean => {
    return rowIndex === 1;
  };

  const isOverlayNeeded = (rowIndex: number): boolean => {
    return isLastRow(rowIndex) && getNumberOfImages() > 3;
  };

  const getClassesForAssetHolder = (rowIndex: number): string => {
    const numberImages = getNumberOfImages();
    if (numberImages === 1) {
      return '';
    }

    let assetHolderStyles = `${styles.multipleImages}`;

    if (isOverlayNeeded(rowIndex)) {
      assetHolderStyles = `${assetHolderStyles} ${styles.overlayHolder}`;
    }

    return assetHolderStyles;
  };

  const getNumberOfImagesPerRow = (numberRows: number): number[] => {
    if (numberRows === 1) {
      return [1];
    }

    const numberImages = getNumberOfImages();
    if (numberImages === 2) {
      return [1, 1];
    }

    return [1, 2];
  };

  const onImageLoad = (loadedPosterHashedId: PosterHashedId): void => {
    onLoad(loadedPosterHashedId, false);
  };

  const onVideoLoad = (loadedPosterHashedId: PosterHashedId): void => {
    onLoad(loadedPosterHashedId, true);
  };

  const onLoad = (loadedPosterHashedId: PosterHashedId, isVideo: boolean): void => {
    pollForPosterPreviewChanges(loadedPosterHashedId, isVideo, true)
      .promise.then(async (): Promise<void> => {
        onAssetLoad(loadedPosterHashedId);
        setIsCacheBustingNeeded(false);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const getClassesForAssetWrapper = (imageNumber: number): string => {
    const isFirstImage = imageNumber === 1;

    if (!isFirstImage && getNumberOfImages() > 2) {
      return `${styles.assetWrapperTwin}`;
    }

    let styleClasses = `${styles.assetWrapper}`;

    if (isGoogleBusinessProfileAccount(account)) {
      styleClasses += ` ${styles.googleBusinessProfile}`;
    }

    if (isThreadsAccount(account) && posterHashedIds.length === 1) {
      styleClasses += ` spacing-m-l-12`;
    }

    return styleClasses;
  };

  const getAssetElement = (imageNumber: number): ReactElement<any> => {
    return (
      <div key={`asset-${imageNumber}`} className={getClassesForAssetWrapper(imageNumber)}>
        {getMedia(imageNumber)}
      </div>
    );
  };

  const getCarouselCounterClasses = (): string => {
    if (isPinterestAccount(account)) {
      return styles.pinterestCarouselCounter;
    }

    return '';
  };

  const getCarouselIndicatorClasses = (): string => {
    if (isPinterestAccount(account)) {
      return styles.pinterestCarouselIndicator;
    }

    return '';
  };

  const getCarouselContainerClasses = (): string => {
    if (isPinterestAccount(account)) {
      return '_borderless';
    }

    return styles.threadsAndInstagramCarouselContainer;
  };

  const getAssetIndex = (assetNumber: number): number => {
    return assetNumber - 1;
  };

  const isLastDisplayablePoster = (assetNumber: number): boolean => {
    return assetNumber === maxNumberDisplayablePosters || assetNumber === getNumberOfImages();
  };

  const getVideoOverlayStyles = (assetNumber: number): string => {
    if (!isLastDisplayablePoster(assetNumber) || getNumberOfImages() <= 3) {
      return `${styles.videoOverlayStyles}`;
    }

    return `_hidden`;
  };

  const getPlayButtonStyles = (assetNumber: number): string => {
    if (!isLastDisplayablePoster(assetNumber) || getNumberOfImages() <= 3) {
      return `${styles.playButtonStyles}`;
    }

    return `_hidden`;
  };

  const getGraphicURL = (url: string): string => {
    if (isCacheBustingNeeded) {
      return getCacheBustedURL(url);
    }

    return url;
  };

  const getCoverURL = (posterGraphicMeta: GraphicMeta): string | undefined => {
    const coverObject = posterGraphicMeta.cover;

    if (!coverObject) {
      return undefined;
    }

    return coverObject.coverUrl;
  };

  const getVideoPreviewWithoutCoverForIOS = (assetNumber: number, posterHashedId: string, graphicURL: string): ReactElement<any> => {
    const FIRST_FRAME_SEC = 0.1;

    return (
      <>
        <video
          id={`preview-grid-image-${assetNumber}`}
          className={`${styles.assetStyles} ${getClassesForAsset(assetNumber)}`}
          ref={videoRef}
          onLoadedData={(e) => {
            seekAndPauseAutoplayVideo(e, FIRST_FRAME_SEC);
            return onVideoLoad(posterHashedId);
          }}
          onError={() => {
            onAssetErrored(posterHashedId);
            doRetryIfNeeded();
          }}
          muted
          loop
          src={graphicURL}
          autoPlay
          playsInline
        />
        <PreviewVideoPlayer videoRef={videoRef} showSoundControls={false} />
      </>
    );
  };

  const getVideoMedia = (assetNumber: number, posterHashedId: string, graphicURL: string, coverUrl: string | undefined): ReactElement<any> => {
    const isAutoPlay = !isLastDisplayablePoster(assetNumber) || getNumberOfImages() <= 3;
    const isOrphanedPreview = !account;

    if (isOrphanedPreview) {
      return (
        <>
          <video
            id={`preview-grid-image-${assetNumber}`}
            className={`${styles.assetStyles} ${getClassesForAsset(assetNumber)}`}
            onLoadedData={() => {
              return onVideoLoad(posterHashedId);
            }}
            onError={() => {
              onAssetErrored(posterHashedId);
              doRetryIfNeeded();
            }}
            autoPlay={isAutoPlay}
            muted
            loop
            src={graphicURL}
          />
        </>
      );
    }

    if (isIOS && !coverUrl) {
      return getVideoPreviewWithoutCoverForIOS(assetNumber, posterHashedId, graphicURL);
    }

    return (
      <>
        <video
          id={`preview-grid-image-${assetNumber}`}
          className={`${styles.assetStyles} ${getClassesForAsset(assetNumber)}`}
          ref={videoRef}
          onLoadedMetadata={() => {
            return onVideoLoad(posterHashedId);
          }}
          onError={() => {
            onAssetErrored(posterHashedId);
            doRetryIfNeeded();
          }}
          poster={coverUrl}
          muted
          loop
          src={graphicURL}
        />
        <PreviewVideoPlayer videoRef={videoRef} showSoundControls={false} />
      </>
    );
  };

  const getMedia = (assetNumber: number): ReactElement<any> => {
    const assetIndex = getAssetIndex(assetNumber);
    const posterHashedId = posterHashedIds[assetIndex];
    const posterGraphicMeta = designsGraphicsMetaState[posterHashedId];

    let isVideo = false;
    let graphicURL = '';
    let coverUrl;

    if (posterGraphicMeta?.graphicURL) {
      isVideo = posterGraphicMeta.isVideo;
      graphicURL = getGraphicURL(posterGraphicMeta.graphicURL);
      coverUrl = getCoverURL(posterGraphicMeta);
    }

    if (isVideo) {
      return getVideoMedia(assetNumber, posterHashedId, graphicURL, coverUrl);
    }

    return (
      <img
        id={`preview-grid-image-${assetNumber}`}
        className={`${styles.assetStyles} ${getClassesForAsset(assetNumber)}`}
        onLoad={() => {
          onImageLoad(posterHashedId);
        }}
        onError={() => {
          onAssetErrored(posterHashedId);
          doRetryIfNeeded();
        }}
        src={graphicURL}
        alt="social-media-graphic"
      />
    );
  };

  const doRetryIfNeeded = (): void => {
    if (doRetryOnError) {
      retrySetGraphicURL();
    }
  };

  const retrySetGraphicURL = (): void => {
    if (areMaxRetriesReached()) {
      return;
    }

    if (!isCacheBustingNeeded) {
      setIsCacheBustingNeeded(true);
    }

    setTimeout((): void => {
      setRetryThroughRefreshNumber(retryThroughRefreshNumber + 1);

      if (areMaxRetriesReached()) {
        setIsCacheBustingNeeded(false);
      }
    }, retryTimeoutInMS);
  };

  const areMaxRetriesReached = (): boolean => {
    return retryThroughRefreshNumber >= maxRetries;
  };

  const getImageNumberInRow = (nRow: number, rowImageIndex: number): number => {
    if (nRow === 0) {
      return 1;
    }

    return 2 + rowImageIndex;
  };

  const getImageGridRow = (nRowImages: number, nRow: number): ReactElement<any>[] => {
    const rowElements = [];

    for (let i = 0; i < nRowImages; i++) {
      const imageNumber = getImageNumberInRow(nRow, i);
      rowElements.push(getAssetElement(imageNumber));
    }

    return rowElements;
  };

  const getNumberHiddenImages = (): number => {
    return getNumberOfImages() - maxNumberDisplayablePosters;
  };

  const getOverlay = (rowIndex: number): ReactElement<any> | null => {
    if (!isOverlayNeeded(rowIndex)) {
      return null;
    }

    return (
      <div className={styles.overlay}>
        <Text size={TextSize.XLARGE} className="content-body-white" bold val={`+${getNumberHiddenImages()}`} />
      </div>
    );
  };

  const getImageGrid = (): ReactElement<any>[] => {
    const numberRows = getNumberOfImages() === minNumberDisplayablePosters ? minNumberRows : maxNumberRows;
    const numberImagesPerRow = getNumberOfImagesPerRow(numberRows);
    const gridElements = [];

    for (let i = 0; i < numberRows; i++) {
      const nImages = numberImagesPerRow[i];
      gridElements.push(
        <div key={`asset-media-grid-${i}`} className={`${styles.assetHolder} ${getClassesForAssetHolder(i)}`}>
          {getImageGridRow(nImages, i)}
          {getOverlay(i)}
        </div>
      );
    }

    return gridElements;
  };

  const getMediaItemsForCarousel = (): MediaItemData[] => {
    const mediaItems: MediaItemData[] = [];

    for (const posterHashedId of posterHashedIds) {
      const graphicMeta = designsGraphicsMetaState[posterHashedId];
      if (graphicMeta) {
        mediaItems.push({
          mediaUrl: getGraphicURL(graphicMeta.graphicURL),
          isVideo: graphicMeta.isVideo,
          externalMediaId: posterHashedId,
        });
      }
    }

    return mediaItems;
  };

  const getContainerForCarouselPost = (): ReactElement<any> => {
    return (
      <SimpleMediaCarousel
        mediaItems={getMediaItemsForCarousel()}
        onIndividualImageLoad={onImageLoad}
        onIndividualVideoLoad={onVideoLoad}
        onIndividualAssetErrored={onAssetErrored}
        doRetryOnIndividualAssetError={doRetryIfNeeded}
        counterClasses={getCarouselCounterClasses()}
        carouselIndicatorClasses={getCarouselIndicatorClasses()}
        containerClasses={getCarouselContainerClasses()}
      />
    );
  };

  if (isOrphanedPreview) {
    return (
      <ClickableDiv className={`${styles.loadingPostHolder} ${getClassesForHolder()} _full-width _cursor-pointer`} onClick={onOrphanedPreviewContainerClick}>
        {getImageGrid()}
      </ClickableDiv>
    );
  }

  if (doesAccountHaveCarouselPosts(account?.type) && isMultiPosting()) {
    return <div className={`${styles.loadingPostHolder} ${getClassesForHolder()} _full-width`}>{getContainerForCarouselPost()}</div>;
  }

  return <div className={`${styles.loadingPostHolder} ${getClassesForHolder()} _full-width`}>{getImageGrid()}</div>;
}
